import { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from '@lexical/list';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
} from '@lexical/rich-text';
import {
  $getSelectionStyleValueForProperty,
  $isParentElementRTL,
  $patchStyleText,
  $setBlocksType,
} from '@lexical/selection';
import {
  $findMatchingParent,
  $getNearestBlockElementAncestorOrThrow,
  $getNearestNodeOfType,
  mergeRegister,
} from '@lexical/utils';
import { $isTableNode, $isTableSelection } from '@lexical/table';
import { $createCodeNode, $isCodeNode, CODE_LANGUAGE_FRIENDLY_NAME_MAP, CODE_LANGUAGE_MAP, getLanguageFriendlyName } from '@lexical/code';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';

import { blockTypeToBlockName, useToolbarState } from '../../context/ToolbarContext';
import DropDown, { DropDownItem } from '../../ui/DropDown';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import ColorPicker from '../../ui/ColorPicker';
import { FontSize } from './fontSize';
import Modal from '../../ui/Modal';
import { INSERT_COLLAPSIBLE_COMMAND } from '../CollapsiblePlugin';
import { INSERT_EXCALIDRAW_COMMAND } from '../ExcalidrawPlugin';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { INSERT_PAGE_BREAK } from '../PageBreakPlugin';
import {
  InsertImageDialog,
  InsertGIFDialog,
  // InsertPollDialog,
  InsertTableDialog,
  InsertEquationDialog,
  InsertColumnsDialog,
  InsertSectionAnchorDialog,
  InsertInternalLinkDialog,
} from '../../ui/InsertDialogs';
import {
  Divider as MuiDivider,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Add,
  ArrowDropDown,
  ArrowRight,
  BorderColor,
  CalendarToday,
  Code as CodeIcon,
  FormatAlignCenter,
  FormatAlignJustify,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatClear,
  FormatColorFill,
  FormatColorText,
  FormatIndentDecrease,
  FormatIndentIncrease,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatSize,
  FormatStrikethrough,
  FormatUnderlined,
  Functions,
  GifBox,
  HorizontalRule,
  Image as ImageIcon,
  InsertPageBreak,
  Link as LinkIcon,
  Looks4,
  Looks3,
  LooksOne,
  LooksTwo,
  // Poll,
  Redo,
  Subject,
  Subscript,
  Superscript,
  TableChart,
  TaskAlt,
  TextFields,
  Abc,
  Undo,
  ViewColumn,
  FontDownload,
  FormatTextdirectionLToR,
  FormatTextdirectionRToL,
  Draw,
  Anchor,
  Tag,
} from '@mui/icons-material';


const FONT_FAMILY_OPTIONS = [
  ['"Segoe UI", Arial, sans-serif', 'Sans'],
  ['Georgia, "Times New Roman", serif', 'Serif'],
  ['"Courier New", monospace', 'Mono'],
  ['"Inter", "Segoe UI", sans-serif', 'Inter'],
  ['"Nunito Sans", "Segoe UI", sans-serif', 'Nunito Sans'],
  ['"DM Sans", "Segoe UI", sans-serif', 'DM Sans'],
  ['"Lora", Georgia, serif', 'Lora'],
  ['"Poppins", "Trebuchet MS", sans-serif', 'Poppins'],
  ['"Montserrat", "Segoe UI", sans-serif', 'Montserrat'],
  ['"Source Sans 3", "Segoe UI", sans-serif', 'Source Sans 3'],
  ['"Roboto Slab", Georgia, serif', 'Roboto Slab'],
  ['"Playfair Display", Georgia, serif', 'Playfair Display'],
  ['"Merriweather", Georgia, serif', 'Merriweather'],
  ['"Ubuntu", "Segoe UI", sans-serif', 'Ubuntu'],
];

// Normalize a CSS font-family
function normalizeFontKey(fontStack) {
  return fontStack.split(',')[0].trim().replace(/["']/g, '').trim().toLowerCase();
}

function getFontFamilyLabel(value) {
  const key = normalizeFontKey(value);
  const match = FONT_FAMILY_OPTIONS.find(([family]) => normalizeFontKey(family) === key);
  if (match) return match[1];
  // Not in the list — show the first font name cleaned of quotes & whitespace
  return value.split(',')[0].trim().replace(/["']/g, '').trim() || value;
}

function getCodeLanguageOptions() {
  const options = [];
  for (const [lang, friendlyName] of Object.entries(CODE_LANGUAGE_FRIENDLY_NAME_MAP)) {
    options.push([lang, friendlyName]);
  }
  return options;
}

const CODE_LANGUAGE_OPTIONS = getCodeLanguageOptions();

const blockTypeToIcon = {
  paragraph: <Subject fontSize="small" />,
  h1: <LooksOne fontSize="small" />,
  h2: <LooksTwo fontSize="small" />,
  h3: <Looks3 fontSize="small" />,
  h4: <Looks4 fontSize="small" />,
  bullet: <FormatListBulleted fontSize="small" />,
  number: <FormatListNumbered fontSize="small" />,
  check: <TaskAlt fontSize="small" />,
  quote: <FormatQuote fontSize="small" />,
  code: <CodeIcon fontSize="small" />,
};

function BlockFormatDropDown({ editor, blockType, rootType, disabled }) {
  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatCheckList = () => {
    if (blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createQuoteNode());
      });
    }
  };

  const formatCode = () => {
    if (blockType !== 'code') {
      editor.update(() => {
        let selection = $getSelection();
        if (selection !== null) {
          if (selection.isCollapsed()) {
            $setBlocksType(selection, () => $createCodeNode());
          } else {
            const textContent = selection.getTextContent();
            const codeNode = $createCodeNode();
            selection.insertNodes([codeNode]);
            selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertRawText(textContent);
            }
          }
        }
      });
    }
  };

  return (
    <BlockFormatMenu
      disabled={disabled}
      blockType={blockType}
      formatParagraph={formatParagraph}
      formatHeading={formatHeading}
      formatBulletList={formatBulletList}
      formatNumberedList={formatNumberedList}
      formatCheckList={formatCheckList}
      formatQuote={formatQuote}
      formatCode={formatCode}
    />
  );
}

function BlockFormatMenu({ disabled, blockType, formatParagraph, formatHeading, formatBulletList, formatNumberedList, formatCheckList, formatQuote, formatCode }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const close = (fn) => { setAnchorEl(null); fn && fn(); };

  const items = [
    { type: 'paragraph', label: 'Normal', icon: <Subject fontSize="small" sx={{ color: ICON_COLOR }} />, action: formatParagraph, shortcut: `${MOD}+${ALT}+0` },
    { type: 'h1', label: 'Heading 1', icon: <LooksOne fontSize="small" sx={{ color: ICON_COLOR }} />, action: () => formatHeading('h1'), shortcut: `${MOD}+${ALT}+1` },
    { type: 'h2', label: 'Heading 2', icon: <LooksTwo fontSize="small" sx={{ color: ICON_COLOR }} />, action: () => formatHeading('h2'), shortcut: `${MOD}+${ALT}+2` },
    { type: 'h3', label: 'Heading 3', icon: <Looks3 fontSize="small" sx={{ color: ICON_COLOR }} />, action: () => formatHeading('h3'), shortcut: `${MOD}+${ALT}+3` },
    { type: 'h4', label: 'Heading 4', icon: <Looks4 fontSize="small" sx={{ color: ICON_COLOR }} />, action: () => formatHeading('h4'), shortcut: `${MOD}+${ALT}+4` },
    { type: 'number', label: 'Numbered List', icon: <FormatListNumbered fontSize="small" sx={{ color: ICON_COLOR }} />, action: formatNumberedList, shortcut: `${MOD}+${SHIFT}+7` },
    { type: 'bullet', label: 'Bullet List', icon: <FormatListBulleted fontSize="small" sx={{ color: ICON_COLOR }} />, action: formatBulletList, shortcut: `${MOD}+${SHIFT}+8` },
    { type: 'check', label: 'Check List', icon: <TaskAlt fontSize="small" sx={{ color: ICON_COLOR }} />, action: formatCheckList, shortcut: `${MOD}+${SHIFT}+9` },
    { type: 'quote', label: 'Quote', icon: <FormatQuote fontSize="small" sx={{ color: ICON_COLOR }} />, action: formatQuote, shortcut: `${MOD}+${SHIFT}+Q` },
    // { type: 'code', label: 'Code Block', icon: <CodeIcon fontSize="small" sx={{ color: ICON_COLOR }} />, action: formatCode, shortcut: `${MOD}+${ALT}+C` },
  ];

  return (
    <>
      <Tooltip title="Block Format" arrow>
        <button
          type="button"
          className="toolbar-item block-controls"
          disabled={disabled}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Formatting options for text style"
          aria-haspopup="true"
          aria-expanded={open}
        >
          {blockTypeToIcon[blockType]}
          <span className="text">{blockTypeToBlockName[blockType]}</span>
          <ArrowDropDown fontSize="small" style={{ marginLeft: -2 }} />
        </button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={menuPaperProps}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.type}
            onClick={() => close(item.action)}
            sx={blockType === item.type ? activeMenuItemSx : menuItemSx}
          >
            {item.icon}
            {item.label}
            {item.shortcut && <Shortcut>{item.shortcut}</Shortcut>}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function Divider() {
  return <div className="divider" />;
}

function ElementFormatDropdown({ editor, value, isRTL, disabled }) {
  return (
    <>
      <ToolbarBtn title={`Left Align (${MOD}+${SHIFT}+L)`} active={value === 'left'} disabled={disabled} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}>
        <FormatAlignLeft fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn title={`Center Align (${MOD}+${SHIFT}+E)`} active={value === 'center'} disabled={disabled} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}>
        <FormatAlignCenter fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn title={`Right Align (${MOD}+${SHIFT}+R)`} active={value === 'right'} disabled={disabled} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}>
        <FormatAlignRight fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn title={`Justify Align (${MOD}+${SHIFT}+J)`} active={value === 'justify'} disabled={disabled} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')}>
        <FormatAlignJustify fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn title="Start Align" active={value === 'start'} disabled={disabled} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'start')}>
        <FormatAlignLeft fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn title="End Align" active={value === 'end'} disabled={disabled} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'end')}>
        <FormatAlignRight fontSize="small" />
      </ToolbarBtn>
      <Divider />
      <ToolbarBtn title={`Outdent (${MOD}+[)`} disabled={disabled} onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}>
        {isRTL ? <FormatIndentIncrease fontSize="small" /> : <FormatIndentDecrease fontSize="small" />}
      </ToolbarBtn>
      <ToolbarBtn title={`Indent (${MOD}+])`} disabled={disabled} onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}>
        {isRTL ? <FormatIndentDecrease fontSize="small" /> : <FormatIndentIncrease fontSize="small" />}
      </ToolbarBtn>
    </>
  );
}

function FontDropDown({ editor, value, styleProp, disabled }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback(
    (option) => {
      editor.update(() => {
        const selection = $getSelection();
        if (selection !== null) {
          $patchStyleText(selection, {
            [styleProp]: option,
          });
        }
      });
    },
    [editor, styleProp]
  );

  const displayLabel = styleProp === 'font-family'
    ? getFontFamilyLabel(value)
    : value;

  const options = styleProp === 'font-family' ? FONT_FAMILY_OPTIONS : [];

  return (
    <>
      <Tooltip title={styleProp === 'font-family' ? 'Font Family' : 'Font Size'} arrow>
        <button
          type="button"
          className={'toolbar-item ' + styleProp}
          disabled={disabled}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={styleProp === 'font-family' ? 'Formatting options for font family' : 'Formatting options for font size'}
          aria-haspopup="true"
          aria-expanded={open}
        >
          {styleProp === 'font-family' && <FontDownload fontSize="small" style={{ color: '#5a5e66' }} />}
          <span className="text" style={styleProp === 'font-family' ? { fontFamily: value } : undefined}>{displayLabel}</span>
          <ArrowDropDown fontSize="small" style={{ marginLeft: -2 }} />
        </button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ ...menuPaperProps, sx: { ...menuPaperProps.sx, maxHeight: 320 } }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {options.map(([option, text]) => (
          <MenuItem
            key={option}
            onClick={() => { setAnchorEl(null); handleClick(option); }}
            sx={value === option ? activeMenuItemSx : menuItemSx}
          >
            <span style={{ fontFamily: option }}>{text}</span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

const THEME = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
  activeBg: '#fff4c6',
  activeHoverBg: '#ffe799',
  hoverBg: 'rgba(35, 37, 54, 0.07)',
  iconDefault: '#4b5563',
  iconActive: '#232536',
  toolbarBorder: '#e2e5ee',
};

const ICON_COLOR = THEME.iconDefault;

const IS_APPLE = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const MOD = IS_APPLE ? '⌘' : 'Ctrl';
const ALT = IS_APPLE ? '⌥' : 'Alt';
const SHIFT = IS_APPLE ? '⇧' : 'Shift';

const Shortcut = ({ children }) => (
  <span style={{ marginLeft: 'auto', paddingLeft: 24, color: '#9ca3af', fontSize: '0.75rem', fontWeight: 400, whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const menuItemSx = { fontSize: '0.8125rem', gap: 1, py: 0.75, justifyContent: 'flex-start' };
const activeMenuItemSx = { ...menuItemSx, backgroundColor: 'rgba(255,207,81,0.18)', '&:hover': { backgroundColor: 'rgba(255,207,81,0.28)' } };

const menuPaperProps = {
  elevation: 3,
  sx: { borderRadius: 1.5, minWidth: 180, mt: 0.5 },
};

const ToolbarBtn = ({ active, title, disabled, onClick, onMouseDown, children }) => (
  <Tooltip title={title} arrow enterDelay={400}>
    <span>
      <IconButton
        size="small"
        disabled={disabled}
        onMouseDown={onMouseDown || ((e) => e.preventDefault())}
        onClick={onClick}
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          color: active ? THEME.iconActive : THEME.iconDefault,
          backgroundColor: active ? THEME.activeBg : 'transparent',
          transition: 'all 0.15s',
          '&:hover': { backgroundColor: active ? THEME.activeHoverBg : THEME.hoverBg },
          '&.Mui-disabled': { opacity: 0.4 },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

function InsertDropDown({ editor, showModal, onUploadImage }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  // Capture the Lexical selection on mousedown so it survives focus loss
  const savedSelRef = useRef(null);

  const close = (fn) => {
    setAnchorEl(null);
    fn && fn();
  };

  const insertHorizontalRule = () => {
    editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined);
  };

  const insertPageBreak = () => {
    editor.dispatchCommand(INSERT_PAGE_BREAK, undefined);
  };

  const insertImage = () => {
    showModal('Insert Image', (onClose) => (
      <InsertImageDialog editor={editor} onClose={onClose} onUploadImage={onUploadImage} />
    ));
  };

  const insertGIF = () => {
    showModal('Insert GIF', (onClose) => (
      <InsertGIFDialog editor={editor} onClose={onClose} />
    ));
  };

  // const insertPoll = () => {
  //   showModal('Insert Poll', (onClose) => (
  //     <InsertPollDialog editor={editor} onClose={onClose} />
  //   ));
  // };

  const insertDate = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const dateStr = new Date().toLocaleDateString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        selection.insertText(dateStr);
      }
    });
  };

  const insertTable = () => {
    showModal('Insert Table', (onClose) => (
      <InsertTableDialog editor={editor} onClose={onClose} />
    ));
  };

  const insertCollapsible = () => {
    editor.dispatchCommand(INSERT_COLLAPSIBLE_COMMAND, undefined);
  };

  const insertColumnsLayout = () => {
    showModal('Insert Columns Layout', (onClose) => (
      <InsertColumnsDialog editor={editor} onClose={onClose} />
    ));
  };

  const insertEquation = () => {
    showModal('Insert Equation', (onClose) => (
      <InsertEquationDialog editor={editor} onClose={onClose} />
    ));
  };

  const insertSectionAnchor = () => {
    showModal('Mark Cross-Reference Target', (onClose) => (
      <InsertSectionAnchorDialog editor={editor} onClose={onClose} savedSel={savedSelRef.current} />
    ));
  };

  const insertInternalLink = () => {
    showModal('Insert Cross-Reference', (onClose) => (
      <InsertInternalLinkDialog editor={editor} onClose={onClose} savedSel={savedSelRef.current} />
    ));
  };

  const menuItemSx = { fontSize: '0.8125rem', gap: 1, py: 0.75 };

  return (
    <>
      <Tooltip title="Insert" arrow>
        <button
          type="button"
          className="toolbar-item spaced"
          onMouseDown={(e) => {
            e.preventDefault();
            let saved = null;
            editor.getEditorState().read(() => {
              const sel = $getSelection();
              if ($isRangeSelection(sel) && !sel.isCollapsed()) {
                saved = {
                  anchorKey: sel.anchor.key,
                  anchorOffset: sel.anchor.offset,
                  anchorType: sel.anchor.type,
                  focusKey: sel.focus.key,
                  focusOffset: sel.focus.offset,
                  focusType: sel.focus.type,
                };
              }
            });
            savedSelRef.current = saved;
          }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Insert specialized editor node"
          aria-haspopup="true"
          aria-expanded={open}
        >
          <Add fontSize="small" />
          <span className="toolbar-item-label">Insert</span>
          <ArrowDropDown fontSize="small" style={{ marginLeft: -2 }} />
        </button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 1.5, minWidth: 210, mt: 0.5 },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => close(insertImage)} sx={menuItemSx}>
          <ImageIcon fontSize="small" sx={{ color: ICON_COLOR }} />
          Image
        </MenuItem>
        <MenuItem onClick={() => close(insertGIF)} sx={menuItemSx}>
          <GifBox fontSize="small" sx={{ color: ICON_COLOR }} />
          GIF
        </MenuItem>
        <MenuItem onClick={() => close(insertTable)} sx={menuItemSx}>
          <TableChart fontSize="small" sx={{ color: ICON_COLOR }} />
          Table
        </MenuItem>
        <MenuItem onClick={() => close(insertCollapsible)} sx={menuItemSx}>
          <ArrowRight fontSize="small" sx={{ color: ICON_COLOR }} />
          Collapsible Container
        </MenuItem>
        <MenuItem onClick={() => close(insertColumnsLayout)} sx={menuItemSx}>
          <ViewColumn fontSize="small" sx={{ color: ICON_COLOR }} />
          Columns Layout
        </MenuItem>
        <MenuItem onClick={() => close(insertEquation)} sx={menuItemSx}>
          <Functions fontSize="small" sx={{ color: ICON_COLOR }} />
          Equation
        </MenuItem>
        <MenuItem onClick={() => close(() => editor.dispatchCommand(INSERT_EXCALIDRAW_COMMAND, undefined))} sx={menuItemSx}>
          <Draw fontSize="small" sx={{ color: ICON_COLOR }} />
          Excalidraw
        </MenuItem>
        {/* <MenuItem onClick={() => close(insertPoll)} sx={menuItemSx}>
          <Poll fontSize="small" sx={{ color: ICON_COLOR }} />
          Poll
        </MenuItem> */}
        <MenuItem onClick={() => close(insertDate)} sx={menuItemSx}>
          <CalendarToday fontSize="small" sx={{ color: ICON_COLOR }} />
          Date
        </MenuItem>
        <MuiDivider />
        <MenuItem onClick={() => close(insertSectionAnchor)} sx={menuItemSx}>
          <Anchor fontSize="small" sx={{ color: ICON_COLOR }} />
          Mark Cross-Reference Target
        </MenuItem>
        <MenuItem onClick={() => close(insertInternalLink)} sx={menuItemSx}>
          <Tag fontSize="small" sx={{ color: ICON_COLOR }} />
          Insert Cross-Reference
        </MenuItem>
        <MuiDivider />
        <MenuItem onClick={() => close(insertHorizontalRule)} sx={menuItemSx}>
          <HorizontalRule fontSize="small" sx={{ color: ICON_COLOR }} />
          Horizontal Rule
        </MenuItem>
        <MenuItem onClick={() => close(insertPageBreak)} sx={menuItemSx}>
          <InsertPageBreak fontSize="small" sx={{ color: ICON_COLOR }} />
          Page Break
        </MenuItem>
      </Menu>
    </>
  );
}

export default function ToolbarPlugin({ setIsLinkEditMode, onUploadImage }) {
  const [editor] = useLexicalComposerContext();
  const { toolbarState, updateToolbarState } = useToolbarState();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const [modal, setModal] = useState(null);

  const {
    blockType,
    rootType,
    canUndo,
    canRedo,
    isBold,
    isItalic,
    isUnderline,
    isStrikethrough,
    isSubscript,
    isSuperscript,
    isCode,
    isLink,
    isHighlight,
    fontFamily,
    fontSize,
    fontColor,
    bgColor,
    elementFormat,
    isRTL,
    codeLanguage,
  } = toolbarState;

  const showModal = useCallback((title, getContent) => {
    setModal({ title, content: getContent(() => setModal(null)) });
  }, []);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
            const parent = e.getParent();
            return parent !== null && $isRootOrShadowRoot(parent);
          });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      updateToolbarState('isRTL', $isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        updateToolbarState('isLink', true);
      } else {
        updateToolbarState('isLink', false);
      }

      // Update table root type
      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        updateToolbarState('rootType', 'table');
      } else {
        updateToolbarState('rootType', 'root');
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          updateToolbarState('blockType', type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          if (type in blockTypeToBlockName) {
            updateToolbarState('blockType', type);
          }
          if ($isCodeNode(element)) {
            const language = element.getLanguage();
            updateToolbarState('codeLanguage', language ? CODE_LANGUAGE_MAP[language] || language : '');
            return;
          }
        }
      }

      // Handle font styles
      updateToolbarState(
        'fontColor',
        $getSelectionStyleValueForProperty(selection, 'color', '#000')
      );
      updateToolbarState(
        'bgColor',
        $getSelectionStyleValueForProperty(selection, 'background-color', '#fff')
      );
      updateToolbarState(
        'fontFamily',
        $getSelectionStyleValueForProperty(selection, 'font-family', '"Segoe UI", Arial, sans-serif')
      );
      updateToolbarState(
        'fontSize',
        $getSelectionStyleValueForProperty(selection, 'font-size', '15px')
      );

      // Handle element format
      let matchingParent;
      if ($isLinkNode(parent)) {
        matchingParent = $findMatchingParent(node, (parentNode) => $isElementNode(parentNode) && !parentNode.isInline());
      }
      updateToolbarState(
        'elementFormat',
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
            ? node.getFormatType()
            : parent?.getFormatType() || 'left'
      );
    }
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      updateToolbarState('isBold', selection.hasFormat('bold'));
      updateToolbarState('isItalic', selection.hasFormat('italic'));
      updateToolbarState('isUnderline', selection.hasFormat('underline'));
      updateToolbarState('isStrikethrough', selection.hasFormat('strikethrough'));
      updateToolbarState('isSubscript', selection.hasFormat('subscript'));
      updateToolbarState('isSuperscript', selection.hasFormat('superscript'));
      updateToolbarState('isCode', selection.hasFormat('code'));
      updateToolbarState('isHighlight', selection.hasFormat('highlight'));
    }
  }, [activeEditor, updateToolbarState]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        setActiveEditor(newEditor);
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    activeEditor.read(() => {
      $updateToolbar();
    });
  }, [activeEditor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable);
      }),
      activeEditor.registerUpdateListener(() => {
        activeEditor.read(() => {
          $updateToolbar();
        });
      }),
      activeEditor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          updateToolbarState('canUndo', payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      activeEditor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          updateToolbarState('canRedo', payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      )
    );
  }, [activeEditor, editor, updateToolbarState, $updateToolbar]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsLinkEditMode(true);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, '');
    } else {
      setIsLinkEditMode(false);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [activeEditor, isLink, setIsLinkEditMode]);

  const onFontColorSelect = useCallback(
    (value) => {
      applyStyleText({ color: value });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeEditor]
  );

  const onBgColorSelect = useCallback(
    (value) => {
      applyStyleText({ 'background-color': value });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeEditor]
  );

  const applyStyleText = useCallback(
    (styles) => {
      activeEditor.update(() => {
        const selection = $getSelection();
        if (selection !== null) {
          $patchStyleText(selection, styles);
        }
      });
    },
    [activeEditor]
  );

  const onCodeLanguageSelect = useCallback(
    (value) => {
      activeEditor.update(() => {
        if (toolbarState.blockType === 'code') {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            const element = $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });
            if ($isCodeNode(element)) {
              element.setLanguage(value);
            }
          }
        }
      });
    },
    [activeEditor, toolbarState.blockType]
  );

  const clearFormatting = useCallback(() => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchor = selection.anchor;
        const focus = selection.focus;
        const nodes = selection.getNodes();
        const extractedNodes = selection.extract();
        if (anchor.key === focus.key && anchor.offset === focus.offset) {
          return;
        }
        nodes.forEach((node, idx) => {
          if ($isTextNode(node)) {
            let textNode = idx === 0 ? extractedNodes[0] : node;
            if (textNode.__style !== '') {
              textNode = textNode.setStyle('');
            }
            if (textNode.__format !== 0) {
              textNode = textNode.setFormat(0);
              $getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
            }
          } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
            node.replace($createParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat('');
          }
        });
      }
    });
  }, [activeEditor]);

  const canViewerSeeInsertDropdown = blockType !== 'code';

  return (
    <div className="toolbar">
      <ToolbarBtn
        title="Undo (Ctrl+Z)"
        disabled={!canUndo || !isEditable}
        onClick={() => activeEditor.dispatchCommand(UNDO_COMMAND, undefined)}
      >
        <Undo fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Redo (Ctrl+Y)"
        disabled={!canRedo || !isEditable}
        onClick={() => activeEditor.dispatchCommand(REDO_COMMAND, undefined)}
      >
        <Redo fontSize="small" />
      </ToolbarBtn>
      <Divider />
      {blockType in blockTypeToBlockName && activeEditor === editor && (
        <>
          <BlockFormatDropDown
            disabled={!isEditable}
            blockType={blockType}
            rootType={rootType}
            editor={activeEditor}
          />
          <Divider />
        </>
      )}
      {blockType === 'code' ? (
        <CodeLanguageDropDown
          disabled={!isEditable}
          codeLanguage={codeLanguage}
          onCodeLanguageSelect={onCodeLanguageSelect}
        />
      ) : (
        <>
          <FontDropDown
            disabled={!isEditable}
            styleProp="font-family"
            value={fontFamily}
            editor={activeEditor}
          />
          <Divider />
          <FontSize
            selectionFontSize={fontSize.slice(0, -2)}
            editor={activeEditor}
            disabled={!isEditable}
          />
          <Divider />
          <ToolbarBtn
            title="Bold (Ctrl+B)"
            active={isBold}
            disabled={!isEditable}
            onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          >
            <FormatBold fontSize="small" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Italic (Ctrl+I)"
            active={isItalic}
            disabled={!isEditable}
            onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          >
            <FormatItalic fontSize="small" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Underline (Ctrl+U)"
            active={isUnderline}
            disabled={!isEditable}
            onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
          >
            <FormatUnderlined fontSize="small" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Code"
            active={isCode}
            disabled={!isEditable}
            onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
          >
            <CodeIcon fontSize="small" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Insert Link"
            active={isLink}
            disabled={!isEditable}
            onClick={insertLink}
          >
            <LinkIcon fontSize="small" />
          </ToolbarBtn>
          <ColorPickerBtn
            title="Text Color"
            disabled={!isEditable}
            icon={<FormatColorText fontSize="small" />}
            color={fontColor}
            onChange={onFontColorSelect}
          />
          <ColorPickerBtn
            title="Background Color"
            disabled={!isEditable}
            icon={<FormatColorFill fontSize="small" />}
            color={bgColor}
            onChange={onBgColorSelect}
          />
          <MoreFormattingDropDown
            disabled={!isEditable}
            activeEditor={activeEditor}
            isStrikethrough={isStrikethrough}
            isSubscript={isSubscript}
            isSuperscript={isSuperscript}
            isHighlight={isHighlight}
            clearFormatting={clearFormatting}
          />
          <Divider />
          {canViewerSeeInsertDropdown && (
            <>
              <InsertDropDown editor={activeEditor} showModal={showModal} onUploadImage={onUploadImage} />
              <Divider />
            </>
          )}
          <ElementFormatDropdown
            disabled={!isEditable}
            value={elementFormat}
            editor={activeEditor}
            isRTL={isRTL}
          />
        </>
      )}
      {modal && (
        <ModalWrapper title={modal.title} onClose={() => setModal(null)}>
          {modal.content}
        </ModalWrapper>
      )}
    </div>
  );
}

function CodeLanguageDropDown({ disabled, codeLanguage, onCodeLanguageSelect }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Code Language" arrow>
        <button
          type="button"
          className="toolbar-item code-language"
          disabled={disabled}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Select language"
          aria-haspopup="true"
          aria-expanded={open}
        >
          <span className="text">{getLanguageFriendlyName(codeLanguage)}</span>
          <ArrowDropDown fontSize="small" style={{ marginLeft: -2 }} />
        </button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ ...menuPaperProps, sx: { ...menuPaperProps.sx, maxHeight: 320 } }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {CODE_LANGUAGE_OPTIONS.map(([value, name]) => (
          <MenuItem
            key={value}
            onClick={() => { setAnchorEl(null); onCodeLanguageSelect(value); }}
            sx={value === codeLanguage ? activeMenuItemSx : menuItemSx}
          >
            {name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function ColorPickerBtn({ title, disabled, icon, color, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <ToolbarBtn
        title={title}
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {icon}
      </ToolbarBtn>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        transitionDuration={0}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 3,
            onMouseDown: (event) => { if (event.target.tagName !== 'INPUT') event.preventDefault(); },
            sx: { borderRadius: 1.5, mt: 0.5, p: 1 },
          },
        }}
      >
        <ColorPicker color={color} onChange={onChange} />
      </Popover>
    </>
  );
}

function MoreFormattingDropDown({ disabled, activeEditor, isStrikethrough, isSubscript, isSuperscript, isHighlight, clearFormatting }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const close = (fn) => { setAnchorEl(null); fn && fn(); };

  const dispatchFormat = (format) => () => {
    activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const transformText = (transformFn) => () => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.getNodes().forEach((node) => {
          if ($isTextNode(node)) {
            node.setTextContent(transformFn(node.getTextContent()));
          }
        });
      }
    });
  };

  const setDirection = (dir) => () => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const seen = new Set();
      selection.getNodes().forEach((node) => {
        const element = $findMatchingParent(
          node,
          (n) => $isElementNode(n) && !$isRootOrShadowRoot(n)
        );
        if (element && !seen.has(element.getKey())) {
          seen.add(element.getKey());
          element.setDirection(dir);
        }
      });
    });
  };

  return (
    <>
      <ToolbarBtn
        title="More Formatting"
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <TextFields fontSize="small" />
      </ToolbarBtn>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={menuPaperProps}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => close(dispatchFormat('strikethrough'))} sx={isStrikethrough ? activeMenuItemSx : menuItemSx}>
          <FormatStrikethrough fontSize="small" sx={{ color: ICON_COLOR }} />
          Strikethrough
          <Shortcut>{MOD}+{SHIFT}+X</Shortcut>
        </MenuItem>
        <MenuItem onClick={() => close(dispatchFormat('subscript'))} sx={isSubscript ? activeMenuItemSx : menuItemSx}>
          <Subscript fontSize="small" sx={{ color: ICON_COLOR }} />
          Subscript
          <Shortcut>{MOD}+,</Shortcut>
        </MenuItem>
        <MenuItem onClick={() => close(dispatchFormat('superscript'))} sx={isSuperscript ? activeMenuItemSx : menuItemSx}>
          <Superscript fontSize="small" sx={{ color: ICON_COLOR }} />
          Superscript
          <Shortcut>{MOD}+.</Shortcut>
        </MenuItem>
        <MenuItem onClick={() => close(dispatchFormat('highlight'))} sx={isHighlight ? activeMenuItemSx : menuItemSx}>
          <BorderColor fontSize="small" sx={{ color: ICON_COLOR }} />
          Highlight
        </MenuItem>
        <MuiDivider />
        <MenuItem onClick={() => close(transformText((t) => t.toLowerCase()))} sx={menuItemSx}>
          <TextFields fontSize="small" sx={{ color: ICON_COLOR }} />
          Lowercase
          <Shortcut>{MOD}+{SHIFT}+1</Shortcut>
        </MenuItem>
        <MenuItem onClick={() => close(transformText((t) => t.toUpperCase()))} sx={menuItemSx}>
          <FormatSize fontSize="small" sx={{ color: ICON_COLOR }} />
          Uppercase
          <Shortcut>{MOD}+{SHIFT}+2</Shortcut>
        </MenuItem>
        <MenuItem onClick={() => close(transformText((t) => t.replace(/\b\w/g, (c) => c.toUpperCase())))} sx={menuItemSx}>
          <Abc fontSize="small" sx={{ color: ICON_COLOR }} />
          Capitalize
          <Shortcut>{MOD}+{SHIFT}+3</Shortcut>
        </MenuItem>
        <MuiDivider />
        <MenuItem onClick={() => close(clearFormatting)} sx={menuItemSx}>
          <FormatClear fontSize="small" sx={{ color: ICON_COLOR }} />
          Clear Formatting
          <Shortcut>{MOD}+\</Shortcut>
        </MenuItem>
        <MuiDivider />
        <MenuItem onClick={() => close(setDirection('ltr'))} sx={menuItemSx}>
          <FormatTextdirectionLToR fontSize="small" sx={{ color: ICON_COLOR }} />
          Left to Right
        </MenuItem>
        <MenuItem onClick={() => close(setDirection('rtl'))} sx={menuItemSx}>
          <FormatTextdirectionRToL fontSize="small" sx={{ color: ICON_COLOR }} />
          Right to Left
        </MenuItem>
      </Menu>
    </>
  );
}

function ModalWrapper({ onClose, children, title }) {
  return (
    <Modal onClose={onClose} title={title} closeOnClickOutside={true}>
      {children}
    </Modal>
  );
}
